import { Fragment, Suspense, lazy, useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { Logo } from "./components/Logo";
import { Seo } from "./components/Seo";
import { LoadingState } from "./components/ui/AsyncState";
import {
  DEFAULT_LOCALE,
  LOCALES,
  LocaleProvider,
  deviceLocale,
  localePath,
  messagesFor,
  preferredLocale,
  readStoredLocale,
  storeLocale,
  type Locale,
} from "./i18n";
import { ArticleIndexPage } from "./pages/ArticleIndexPage";
import { ArticlePage } from "./pages/ArticlePage";
import { FaqPage } from "./pages/FaqPage";
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { buildHomeHead } from "./seo";
import { ARTICLES_PATH, FAQ_PATH } from "./shared";
import { useAppStore } from "./store/appStore";

/**
 * The sheet pages load on demand; the pages a stranger arrives on do not.
 *
 * Someone who found a guide in a search result has no use for the sheet
 * renderer, the QR encoder, or the speech layer, and making them download all
 * of it before the article becomes interactive is a cost paid by exactly the
 * visitors who have not decided anything yet. The homepage and the reading
 * pages stay eager, because one of them is always the first paint.
 */
const TodayPage = lazy(() =>
  import("./pages/TodayPage").then((module) => ({ default: module.TodayPage })),
);
const WeekPage = lazy(() =>
  import("./pages/WeekPage").then((module) => ({ default: module.WeekPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const RecoveryPage = lazy(() =>
  import("./pages/RecoveryPage").then((module) => ({
    default: module.RecoveryPage,
  })),
);

/** Static, so the effect that applies one does not fire on every render. */
const HOME_HEADS: Record<Locale, ReturnType<typeof buildHomeHead>> = {
  en: buildHomeHead("en"),
  nl: buildHomeHead("nl"),
  th: buildHomeHead("th"),
};

function Booting({ label }: { label: string }) {
  return (
    <main className="boot">
      <Logo />
      <LoadingState label={label} />
    </main>
  );
}

function RequireSettings() {
  const settings = useAppStore((state) => state.settings);
  return settings ? <Outlet /> : <Navigate to="/" replace />;
}

/**
 * The gate that waits for local storage before showing anything that depends on
 * it. Only the app sits behind it. The reading pages are outside on purpose:
 * they are the pages a stranger arrives on from a search result, they are
 * served as prerendered HTML, and making them wait for a store they never read
 * would replace that HTML with a loading state the moment the bundle woke up.
 */
function RequireHydration({ label }: { label: string }) {
  const hydrationState = useAppStore((state) => state.hydrationState);

  if (hydrationState === "idle") return <Booting label={label} />;
  if (hydrationState === "corrupt") return <RecoveryPage />;

  return <Outlet />;
}

/**
 * Sends a first-time visitor to the language their device asks for.
 *
 * It runs on the English homepage and nowhere else, and only when nothing has
 * been chosen before. That narrowness is the point. Google's guidance is
 * against redirecting on a guessed language, because a crawler that is bounced
 * out of a page never indexes it; here every language keeps its own URL, every
 * one of those URLs is reachable without JavaScript, they are declared to each
 * other with hreflang, and `/` is the x-default that a language-neutral visitor
 * is meant to land on. A reader who picks a language by hand outranks the
 * device from then on, because the choice is stored.
 *
 * The device, not the country: a Thai speaker in Rotterdam gets Thai.
 */
function DeviceLanguageEntry() {
  const navigate = useNavigate();

  useEffect(() => {
    if (readStoredLocale()) return;

    const device = deviceLocale();
    if (!device || device === DEFAULT_LOCALE) return;

    storeLocale(device);
    navigate(localePath(device, "/"), { replace: true });
  }, [navigate]);

  return null;
}

/** The three public pages, in one language. */
function publicRoutes(locale: Locale) {
  const home = (
    <LocaleProvider locale={locale}>
      <Seo head={HOME_HEADS[locale]} />
      {locale === DEFAULT_LOCALE ? <DeviceLanguageEntry /> : null}
      <HomePage />
    </LocaleProvider>
  );

  // A Fragment rather than a wrapper Route: `Routes` recurses into fragments,
  // and a pathless Route here would become a layout every child sits inside.
  return (
    <Fragment key={locale}>
      <Route
        path={localePath(locale, ARTICLES_PATH)}
        element={
          <LocaleProvider locale={locale}>
            <ArticleIndexPage />
          </LocaleProvider>
        }
      />
      <Route
        path={localePath(locale, FAQ_PATH)}
        element={
          <LocaleProvider locale={locale}>
            <FaqPage />
          </LocaleProvider>
        }
      />
      <Route element={<RequireHydration label={messagesFor(locale).app.booting} />}>
        <Route path={localePath(locale, "/")} element={home} />
      </Route>
    </Fragment>
  );
}

export default function App() {
  const hydrate = useAppStore((state) => state.hydrate);
  /**
   * The app's own pages carry no locale in their URL: they are noindex, and a
   * second copy of every private route would be three times the routing for
   * something no crawler may look at. They follow the reader's choice, then the
   * device. Read once, so a re-render cannot move the app mid-session.
   */
  const [appLocale] = useState<Locale>(() => preferredLocale());
  const appMessages = messagesFor(appLocale);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <Suspense fallback={<Booting label={appMessages.app.booting} />}>
      <Routes>
        {/* English articles, at one URL, whatever language the frame is in. */}
        <Route
          path={`${ARTICLES_PATH}/:slug`}
          element={
            <LocaleProvider locale={DEFAULT_LOCALE}>
              <ArticlePage />
            </LocaleProvider>
          }
        />

        {LOCALES.map((locale) => publicRoutes(locale))}

        <Route
          element={
            <LocaleProvider locale={appLocale}>
              <RequireHydration label={appMessages.app.booting} />
            </LocaleProvider>
          }
        >
          <Route element={<RequireSettings />}>
            <Route element={<AppShell />}>
              <Route path="/today" element={<TodayPage today />} />
              <Route path="/day/:day" element={<TodayPage />} />
              <Route path="/week" element={<WeekPage />} />
              {/* Printed sheets and old bookmarks still point at the day list. */}
              <Route path="/days" element={<Navigate to="/week" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
          </Route>
        </Route>

        {/*
          Outside the hydration gate, like the reading pages. A 404 is served as
          its own prerendered document, and making it wait for local storage
          would replace that document with a loading state before showing the
          same words again.
        */}
        <Route
          path="*"
          element={
            <LocaleProvider locale={appLocale}>
              <NotFoundPage />
            </LocaleProvider>
          }
        />
      </Routes>
    </Suspense>
  );
}
